import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from "react-hot-toast";
import { KTCard } from '../../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../../_metronic/helpers/components/KTCardBody';
import Content from '../../../_metronic/layout/components/content/Content';
import { PageTitle } from '../../../_metronic/layout/core';
import { ToolbarWrapper } from '../../../_metronic/layout/components/toolbar';
import { storeActivityIndicator } from '../../../redux/reducers/authSlice';
import SiparisTuruSecici from '../../../components/SiparisTuruSecici';
import CustomSingleSelect, { CustomSingleSelectModel } from '../../../components/CustomSingleSelect';
import { CompanyService } from '../../../services/CompanyService';
import { SABIT_GELIR_TURLERI, SabitGelirHesapSonucu, SabitGelirSatir, SabitGelirService, SabitGelirSiparis as SiparisModel, donemAdi, sabitGelirTurAdi, tl } from '../../../services/SabitGelirService';

// Uyarı rengi (09.10.2026): açık sarı zeminde sarı yazı okunmuyordu; Sipariş türü seçicisiyle aynı mat turuncu.
const UYARI_STILI = { background: '#FFF6EE', color: '#A75A24', border: '1px solid #DD8A4E' };

const buAy = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

/**
 * Nakliye siparişi, tür "Aylık Sabit Gelir" (08.10.2026). Dönem + Müşteri + Gelir türü + PO No seçilir; satırlar Sabit Gelir
 * Dağıtımı kartlarından sunucuda hesaplanır. Aynı Müşteri + PO No + Tür + Dönem için tek sipariş açılır. Sipariş
 * Faturalanacaklar'a düşer ve bugünkü gibi faturalanır. Kayıtlı sipariş ?id= ile salt okunur açılır.
 */
const SabitGelirSiparis = () => {
    const pageName = "Kargo Modülü > Nakliye > Siparişler"
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id') ?? undefined;

    const [donem, setDonem] = useState(buAy());
    const [musteri, setMusteri] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [tur, setTur] = useState<number>(1);
    const [poNumaralari, setPoNumaralari] = useState<string[]>([]);
    const [poNo, setPoNo] = useState('');
    const [hesap, setHesap] = useState<SabitGelirHesapSonucu | undefined>(undefined);
    const [siparis, setSiparis] = useState<SiparisModel | undefined>(undefined);
    // Satır seçimi (diğer gridlerdeki gibi onay kutusu); hesap ya da sipariş değişince temizlenir.
    const [seciliSatirlar, setSeciliSatirlar] = useState<number[]>([]);

    useEffect(() => { setSeciliSatirlar([]); }, [hesap, siparis])

    const yil = Number(donem.split('-')[0]), ay = Number(donem.split('-')[1]);

    const yukleniyor = async <T,>(is_: () => Promise<T>) => {
        try {
            dispatch(storeActivityIndicator(true));
            return await is_();
        } catch (error: any) {
            toast.error(error);
            return undefined;
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    // Kayıtlı aylık sabit gelir siparişi normal Siparişler ekranında açılır (Burak, 08.10.2026).
    useEffect(() => {
        if (id)
            navigate('/ambar/listadd?id=' + id, { replace: true });
    }, [id])

    // Müşteri ya da tür değişince o müşterinin kartlarındaki PO numaraları gelir; tek PO varsa seçili gelir.
    useEffect(() => {
        setHesap(undefined);

        if (!musteri) {
            setPoNumaralari([]);
            setPoNo('');
            return;
        }

        yukleniyor(async () => {
            const r = await SabitGelirService.poNumaralari(musteri.value, tur);
            if (!r.isSuccess) { toast.error(r.message ?? ''); return; }
            setPoNumaralari(r.data);
            setPoNo(r.data.length === 1 ? r.data[0] : '');
        });
    }, [musteri, tur])

    useEffect(() => { setHesap(undefined); }, [donem, poNo])

    const istek = () => ({ customerId: musteri?.value, poNo, tur, donemYil: yil, donemAy: ay });

    const getir = async () => {
        if (!musteri) { toast.error('Müşteriyi seçiniz!'); return; }
        if (!poNo) { toast.error('PO No seçiniz!'); return; }

        await yukleniyor(async () => {
            const r = await SabitGelirService.hesapla(istek());
            if (!r.isSuccess) { toast.error(r.message ?? ''); return; }
            setHesap(r.data);
            if (r.data.satirlar.length === 0) toast.error('Bu seçim için geçerli dağıtım satırı yok.');
        });
    }

    const olustur = async () => {
        await yukleniyor(async () => {
            const r = await SabitGelirService.siparisOlustur(istek());
            if (!r.isSuccess) { toast.error(r.message ?? ''); return; }
            toast.success(`Sipariş oluşturuldu: ${r.data.refNo}. Faturalanacaklar'da listelenir.`);
            navigate('/ambar/listadd?id=' + r.data.ambarId, { replace: true });
        });
    }

    const yenile = async () => {
        if (!siparis) return;
        await yukleniyor(async () => {
            const r = await SabitGelirService.siparisYenile(siparis.ambarId);
            if (!r.isSuccess) { toast.error(r.message ?? ''); return; }
            setSiparis(r.data);
            toast.success('Satırlar güncel anlaşma ve oranlarla yeniden yazıldı.');
        });
    }

    const satirTablosu = (satirlar: SabitGelirSatir[], faturaDurumu: boolean) => {
        const toplam = satirlar.reduce((a, s) => a + s.tutar, 0), kdv = satirlar.reduce((a, s) => a + s.kdv, 0);
        const tumuSecili = satirlar.length > 0 && seciliSatirlar.length == satirlar.length;
        const satirSec = (i: number) => setSeciliSatirlar(seciliSatirlar.includes(i) ? seciliSatirlar.filter(x => x != i) : [...seciliSatirlar, i]);
        return (
            <div className='table-responsive'>
                <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                    <thead>
                        <tr className='text-start text-muted fw-bolder text-uppercase'>
                            <th style={{ width: 1 }}>
                                <div className='d-flex'>
                                    <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                        <input onChange={() => setSeciliSatirlar(tumuSecili ? [] : satirlar.map((_, i) => i))} className='form-check-input' type='checkbox' checked={tumuSecili} />
                                    </label>
                                </div>
                            </th>
                            <th>Bölüm</th>
                            <th>PO (hizmet kalemi)</th>
                            <th>Açıklama</th>
                            <th className='text-end'>Miktar</th>
                            <th className='text-end'>Birim fiyat</th>
                            <th className='text-end'>Tutar</th>
                            <th className='text-end'>KDV</th>
                            <th className='text-center'>Tevkifat</th>
                            {faturaDurumu && <th className='text-center'>Fatura</th>}
                        </tr>
                    </thead>
                    <tbody className='text-gray-700'>
                        {
                            satirlar.map((s, i) => (
                                <tr key={i} style={{ background: seciliSatirlar.includes(i) ? 'rgba(204, 204, 204, 0.25)' : undefined }}>
                                    <td style={{ width: 1 }}>
                                        <div className='d-flex'>
                                            <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                <input onChange={() => satirSec(i)} className='form-check-input' type='checkbox' checked={seciliSatirlar.includes(i)} />
                                            </label>
                                        </div>
                                    </td>
                                    <td>{s.divisionName}</td>
                                    <td>{s.poName}</td>
                                    <td className='fs-7'>{s.aciklama}</td>
                                    <td className='text-end'>{s.miktar}</td>
                                    <td className='text-end text-nowrap'>{tl(s.birimFiyat)}</td>
                                    <td className='text-end text-nowrap'>{tl(s.tutar)} {s.currencyTypeName}</td>
                                    <td className='text-end text-nowrap'>{tl(s.kdv)} <span className='text-muted fs-8'>%{s.kdvOrani}</span></td>
                                    <td className='text-center'>{s.tevkifatli ? <span className='badge badge-light-primary'>9/10</span> : <span className='badge badge-light'>Yok</span>}</td>
                                    {faturaDurumu && <td className='text-center'>{s.faturalandi ? <span className='badge badge-light-success'>Faturalandı</span> : <span className='badge' style={UYARI_STILI}>Bekliyor</span>}</td>}
                                </tr>
                            ))
                        }
                    </tbody>
                    {/* Dip toplam: diğer form tablolarındaki gibi üstte ayırıcı çizgi ve ayrı zemin rengi (ebbe-dip-toplam). */}
                    <tfoot>
                        <tr className='ebbe-dip-toplam'>
                            <td style={{ width: 1 }}></td>
                            <td colSpan={5}>Toplam</td>
                            <td className='text-end text-nowrap'>{tl(toplam)}</td>
                            <td className='text-end text-nowrap'>{tl(kdv)}</td>
                            <td colSpan={faturaDurumu ? 2 : 1}></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        )
    }

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Content>
                {
                    siparis
                        ? <KTCard>
                            <KTCardBody>
                                <div className='d-flex flex-wrap align-items-center gap-3 mb-6'>
                                    <h4 className='text-black m-0'>Sipariş {siparis.refNo}</h4>
                                    <span className='badge badge-light-primary'>Aylık Sabit Gelir</span>
                                    <span className='badge badge-light'>{sabitGelirTurAdi(siparis.tur)}</span>
                                    <span className='badge badge-light'>{donemAdi(siparis.donemYil, siparis.donemAy)}</span>
                                    <div className='ms-auto d-flex gap-2'>
                                        <button type='button' className='btn btn-sm btn-light' onClick={() => navigate('/ambar/list')}>Siparişler</button>
                                        <button type='button' className='btn btn-sm btn-light-primary' disabled={siparis.faturalanmisSatirVar} title={siparis.faturalanmisSatirVar ? 'Faturalanmış satır var; yenilenemez' : 'Güncel anlaşma ve oranlarla yeniden yaz'} onClick={yenile}>Satırları yenile</button>
                                        <button type='button' className='btn btn-sm btn-primary' onClick={() => navigate('/willinvoiced/list')}>Faturalanacaklar</button>
                                    </div>
                                </div>
                                <div className='row g-5 mb-6 text-gray-800'>
                                    <div className='col-md-5'><div className='text-muted fs-7'>Müşteri</div><div className='fw-bold'>{siparis.customerName}</div></div>
                                    <div className='col-md-3'><div className='text-muted fs-7'>PO No</div><div className='fw-bold'>{siparis.poNo}</div></div>
                                    <div className='col-md-2'><div className='text-muted fs-7'>Sipariş tarihi</div><div className='fw-bold'>{siparis.orderDateStr}</div></div>
                                </div>
                                {satirTablosu(siparis.satirlar, true)}
                            </KTCardBody>
                        </KTCard>
                        : <React.Fragment>
                            <KTCard className='mb-5'>
                                <KTCardBody>
                                    <div className='d-flex align-items-center gap-3 mb-6'>
                                        <h4 className='text-black m-0'>Sipariş Ekle</h4>
                                        <div className='ms-2'>
                                            <SiparisTuruSecici secili={2} onChange={() => navigate('/ambar/listadd')} />
                                        </div>
                                    </div>
                                    <div className='row g-5 align-items-end'>
                                        <div className='col-md-2'>
                                            <label className='form-label required'>Dönem</label>
                                            <input type='month' className='form-control form-control-solid' value={donem} onChange={(e) => e.target.value && setDonem(e.target.value)} />
                                        </div>
                                        <div className='col-md-4'>
                                            <CustomSingleSelect
                                                labelText='Müşteri'
                                                isLabelRequired
                                                selectedValue={musteri}
                                                onChange={setMusteri}
                                                onMenuOpen={() => CompanyService.getCompanyList({ isCustomer: true })}
                                                labelName='name'
                                                valueName='companyId'
                                            />
                                        </div>
                                        <div className='col-md-2'>
                                            <label className='form-label required'>Gelir türü</label>
                                            <select className='form-select form-select-solid' value={tur} onChange={(e) => setTur(Number(e.target.value))}>
                                                {SABIT_GELIR_TURLERI.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
                                            </select>
                                        </div>
                                        <div className='col-md-2'>
                                            <label className='form-label required'>PO No</label>
                                            <select className='form-select form-select-solid' value={poNo} onChange={(e) => setPoNo(e.target.value)} disabled={!musteri}>
                                                <option value=''>{!musteri ? 'Önce müşteri' : poNumaralari.length ? 'Seçiniz' : 'Kart yok'}</option>
                                                {poNumaralari.map(x => <option key={x} value={x}>{x}</option>)}
                                            </select>
                                        </div>
                                        <div className='col-md-2'>
                                            <button type='button' className='btn btn-primary w-100' onClick={getir}>Sabit gelirleri getir</button>
                                        </div>
                                    </div>
                                    <div className='form-text mt-3'>Satırlar Kargo Modülü › Nakliye › Sabit Gelir Dağıtımı kartlarından, {donemAdi(yil, ay)} ayının son günü geçerli anlaşma fiyatıyla hesaplanır. Kira ve personel ayrı sipariş ve ayrı fatura olur.</div>
                                </KTCardBody>
                            </KTCard>

                            {
                                hesap && (
                                    <KTCard>
                                        <KTCardBody>
                                            <div className='d-flex align-items-center mb-4'>
                                                <h4 className='text-black m-0 flex-grow-1'>Beklenen gelir · {donemAdi(yil, ay)} · {sabitGelirTurAdi(tur)}</h4>
                                                {
                                                    hesap.mevcutSiparisId
                                                        ? <button type='button' className='btn btn-sm fw-bold' style={UYARI_STILI} onClick={() => navigate('/ambar/listadd?id=' + hesap.mevcutSiparisId)}>Bu dönem için sipariş var: {hesap.mevcutSiparisRefNo} — aç</button>
                                                        : <button type='button' className='btn btn-sm btn-success' disabled={hesap.satirlar.length === 0} onClick={olustur}>Siparişi oluştur</button>
                                                }
                                            </div>
                                            {
                                                hesap.uyarilar.length > 0 && (
                                                    <div className='alert py-3' style={UYARI_STILI}>
                                                        {hesap.uyarilar.map((u, i) => <div key={i}>{u}</div>)}
                                                    </div>
                                                )
                                            }
                                            {hesap.satirlar.length > 0 ? satirTablosu(hesap.satirlar, false) : <div className='text-muted'>Bu müşteri, PO No, gelir türü ve dönem için geçerli dağıtım satırı yok.</div>}
                                        </KTCardBody>
                                    </KTCard>
                                )
                            }
                        </React.Fragment>
                }
            </Content>
        </React.Fragment>
    )
}

export default SabitGelirSiparis;
